import type { Lane, RunnerConfig } from './types';

export const PHYSICS_PRESET_IDS = ['relaxed', 'classic', 'snappy', 'turbo'] as const;

export type PhysicsPresetId = (typeof PHYSICS_PRESET_IDS)[number];

export interface PhysicsPreset {
  readonly id: PhysicsPresetId;
  readonly label: string;
  readonly description: string;
  readonly config: RunnerConfig;
}

export type RunnerConfigOverrides = Partial<Omit<RunnerConfig, 'laneCenters'>> &
  Readonly<{ laneCenters?: Readonly<Partial<Record<Lane, number>>> }>;

const BASE_RUNNER_CONFIG = {
  fixedStep: 1 / 60,
  countdownDuration: 3,
  laneCenters: {
    [-1]: -2.4,
    [0]: 0,
    [1]: 2.4,
  },
  laneChangeDuration: 0.18,
  inputBufferDuration: 0.1,
  jumpVelocity: 8,
  gravity: 20,
  slideDuration: 0.5,
  baseSpeed: 10,
  maxSpeed: 18,
  speedGrowthInterval: 10,
  speedGrowthFactor: 1.02,
  coinValue: 10,
  chunkLength: 36,
  chunksAhead: 6,
  recycleBehindZ: 24,
  playerWidth: 0.75,
  playerDepth: 0.8,
  standingHeight: 1.8,
  slidingHeight: 0.75,
  laneCollisionWidth: 0.85,
} as const satisfies RunnerConfig;

const POSITIVE_FIELDS = [
  'fixedStep',
  'countdownDuration',
  'laneChangeDuration',
  'inputBufferDuration',
  'jumpVelocity',
  'gravity',
  'slideDuration',
  'baseSpeed',
  'maxSpeed',
  'speedGrowthInterval',
  'speedGrowthFactor',
  'coinValue',
  'chunkLength',
  'chunksAhead',
  'recycleBehindZ',
  'playerWidth',
  'playerDepth',
  'standingHeight',
  'slidingHeight',
  'laneCollisionWidth',
] as const satisfies readonly (keyof RunnerConfig)[];

export function validateRunnerConfig(config: RunnerConfig): readonly string[] {
  const errors: string[] = [];

  for (const field of POSITIVE_FIELDS) {
    if (!Number.isFinite(config[field]) || config[field] <= 0) {
      errors.push(`${field} must be greater than zero`);
    }
  }

  if (config.fixedStep > 1 / 30) errors.push('fixedStep must be at least 30 Hz');
  if (config.maxSpeed < config.baseSpeed) errors.push('maxSpeed must be at least baseSpeed');
  if (config.speedGrowthFactor < 1) errors.push('speedGrowthFactor must be at least 1');
  if (!Number.isInteger(config.chunksAhead)) errors.push('chunksAhead must be an integer');
  if (config.slidingHeight >= config.standingHeight) {
    errors.push('slidingHeight must be lower than standingHeight');
  }
  if (
    config.laneCenters[-1] >= config.laneCenters[0] ||
    config.laneCenters[0] >= config.laneCenters[1]
  ) {
    errors.push('laneCenters must increase from left to right');
  }

  const jumpPeak = config.jumpVelocity ** 2 / (2 * config.gravity);
  if (jumpPeak <= 0.9) errors.push('jump arc must clear the 0.9-unit barrier');

  return errors;
}

export function createRunnerConfig(overrides: RunnerConfigOverrides = {}): RunnerConfig {
  const config: RunnerConfig = {
    ...BASE_RUNNER_CONFIG,
    ...overrides,
    laneCenters: {
      ...BASE_RUNNER_CONFIG.laneCenters,
      ...overrides.laneCenters,
    },
  };
  const errors = validateRunnerConfig(config);
  if (errors.length > 0) throw new Error(`Invalid runner config: ${errors.join('; ')}`);
  Object.freeze(config.laneCenters);
  return Object.freeze(config);
}

function definePhysicsPreset(
  id: PhysicsPresetId,
  label: string,
  description: string,
  overrides: RunnerConfigOverrides,
): PhysicsPreset {
  return Object.freeze({ id, label, description, config: createRunnerConfig(overrides) });
}

export const PHYSICS_PRESETS = Object.freeze({
  relaxed: definePhysicsPreset(
    'relaxed',
    'Relaxed',
    'Slower reactions and a floatier jump for learning the controls.',
    {
      laneChangeDuration: 0.22,
      jumpVelocity: 8,
      gravity: 18,
      slideDuration: 0.58,
      baseSpeed: 9,
      maxSpeed: 16,
      speedGrowthInterval: 12,
      speedGrowthFactor: 1.015,
    },
  ),
  classic: definePhysicsPreset(
    'classic',
    'Classic',
    'The original proof-of-concept timing and speed.',
    {},
  ),
  snappy: definePhysicsPreset(
    'snappy',
    'Snappy',
    'Aggressive lane response, compact action arcs, and a high-energy default run.',
    {
      laneChangeDuration: 0.095,
      inputBufferDuration: 0.13,
      jumpVelocity: 15,
      gravity: 78,
      slideDuration: 0.3,
      baseSpeed: 17,
      maxSpeed: 31,
      speedGrowthInterval: 5,
      speedGrowthFactor: 1.045,
    },
  ),
  turbo: definePhysicsPreset(
    'turbo',
    'Turbo',
    'An intentionally intense profile for stress-testing readability and reaction time.',
    {
      laneChangeDuration: 0.12,
      inputBufferDuration: 0.12,
      jumpVelocity: 11,
      gravity: 42,
      slideDuration: 0.34,
      baseSpeed: 16,
      maxSpeed: 30,
      speedGrowthInterval: 6,
      speedGrowthFactor: 1.04,
    },
  ),
} as const satisfies Readonly<Record<PhysicsPresetId, PhysicsPreset>>);

export const DEFAULT_PHYSICS_PRESET_ID: PhysicsPresetId = 'snappy';

export const RUNNER_CONFIG = PHYSICS_PRESETS[DEFAULT_PHYSICS_PRESET_ID].config;

export function isPhysicsPresetId(value: string): value is PhysicsPresetId {
  return PHYSICS_PRESET_IDS.some((id) => id === value);
}

export function getPhysicsPreset(requested: string | null | undefined): PhysicsPreset {
  return requested !== undefined && requested !== null && isPhysicsPresetId(requested)
    ? PHYSICS_PRESETS[requested]
    : PHYSICS_PRESETS[DEFAULT_PHYSICS_PRESET_ID];
}
